import type { ReactElement } from "react"
import TextAtm from "@onestore/hel/dist/components/atoms/TextAtm"
import {
  type ConfigsHookResult,
  getResourceValueByLimits,
} from "@gatsby-plugin-generic-page/hooks/useTableConfigs"
import { useConfigsHookContext } from "@gatsby-plugin-generic-page/sections/TableHorizontalBoxesSection"
import isEmpty from "~/lib/isEmpty"

type Props = {
  productId: string
  resourceCategoryId: string
  withoutStyles?: boolean
}

const resourceIPCategoryId = "150"
const resourceIPAZCategoryId = "1136"

export function getResourceCategoryCellString(
  productId: string,
  resourceCategoryId: string,
  getResourceCategory?: ConfigsHookResult["getResourceCategory"],
  getProductResource?: ConfigsHookResult["getProductResource"],
  getProductPeriodResource?: ConfigsHookResult["getProductPeriodResource"],
  getConfigResource?: ConfigsHookResult["getConfigResource"]
): string {
  const resourceCategoryData = getResourceCategory
    ? getResourceCategory(resourceCategoryId, productId)
    : null

  if (isEmpty(resourceCategoryData)) {
    return resourceCategoryId === resourceIPCategoryId ? "1/1" : "-"
  }

  const resourcesElements = resourceCategoryData.resources.map((resource) => {
    const configResource = getConfigResource
      ? getConfigResource(resource.id as string)
      : null

    const value = configResource?.value

    const resourceData = getProductResource
      ? getProductResource(productId, resourceCategoryId, resource.id as string)
      : null

    const resourcePeriodData = getProductPeriodResource
      ? getProductPeriodResource(productId, resource.id as string)
      : null

    if (isEmpty(resourceData) || isEmpty(resourcePeriodData)) {
      return resourceCategoryId === resourceIPCategoryId ? "1/1" : "-"
    }

    const resourceValue = getResourceValueByLimits(
      resourcePeriodData.lower_limit,
      resourcePeriodData.upper_limit,
      value
    )

    /* Fix in ONESTORE-623 */
    return resourceCategoryId === resourceIPCategoryId ||
      resourceCategoryId === resourceIPAZCategoryId
      ? `${resourceValue + 1}/${resourcePeriodData.upper_limit + 1}`
      : `${resourceValue}/${resourcePeriodData.upper_limit}`
  })

  return resourcesElements.join(", ")
}

export default function ResourceCategoryCell({
  productId,
  resourceCategoryId,
}: Props): ReactElement<Props> {
  const {
    getResourceCategory,
    getProductResource,
    getProductPeriodResource,
    getConfigResource,
  } = useConfigsHookContext() ?? {}

  const resourceCategoryData = getResourceCategory
    ? getResourceCategory(resourceCategoryId, productId)
    : null

  if (isEmpty(resourceCategoryData)) {
    return resourceCategoryId === resourceIPCategoryId ? (
      <TextAtm typography="small3">1/1</TextAtm>
    ) : (
      <>-</>
    )
  }

  const resourcesElements = resourceCategoryData.resources.map((resource) => {
    const configResource = getConfigResource
      ? getConfigResource(resource.id as string)
      : null

    const value = configResource?.value

    const resourceData = getProductResource
      ? getProductResource(productId, resourceCategoryId, resource.id as string)
      : null

    const resourcePeriodData = getProductPeriodResource
      ? getProductPeriodResource(productId, resource.id as string)
      : null

    if (isEmpty(resourceData) || isEmpty(resourcePeriodData)) {
      return resourceCategoryId === resourceIPCategoryId ? (
        <TextAtm typography="small3">1/1</TextAtm>
      ) : (
        <>-</>
      )
    }

    const resourceValue = getResourceValueByLimits(
      resourcePeriodData.lower_limit,
      resourcePeriodData.upper_limit,
      value
    )

    return (
      <TextAtm
        key={`resource-${resourceData.name}-${productId}`}
        typography="small2"
      >
        {/* Fix later - ONESTORE-623 */}
        {resourceCategoryId === resourceIPCategoryId ||
        resourceCategoryId === resourceIPAZCategoryId ? (
          <TextAtm typography="small3">{`${resourceValue + 1}/${resourcePeriodData.upper_limit + 1}`}</TextAtm>
        ) : (
          <TextAtm typography="small3">{`${resourceValue}/${resourcePeriodData.upper_limit}`}</TextAtm>
        )}
      </TextAtm>
    )
  })

  return <>{resourcesElements}</>
}
